import { API, graphqlOperation } from 'aws-amplify';

import { runDqfAudit } from '../../../graphql/customMutations';
import { getDriverQualification, listDqfAuditsByDqfId } from '../../../graphql/queries';
import { onUpdateDqfAudit } from '../../../graphql/subscriptions';

// CP-836 slice 2 — AI Review reads the persisted DqfAudit records for a DQF (newest first) and the
// DQF header. Display-only; the audit is kicked off elsewhere (slice 3 / the assemble page).

export const getDqfAudits = async dqfId => {
  try {
    const response = await API.graphql({
      query: listDqfAuditsByDqfId,
      variables: { dqfId, sortDirection: 'DESC', limit: 100 },
    });
    return response.data?.listDqfAuditsByDqfId?.items || [];
  } catch (e) {
    console.log(e);
    return [];
  }
};

// Kick off a new evaluation — used when arriving from Evaluate-to-Send (started here, after AI
// Review's own verifyRoute). Server-side duplicate-guarded: returns the in-flight record if one is
// already PROCESSING for this DQF. Returns the created/existing DqfAudit (PROCESSING) or null.
export const startDqfAudit = async dqfId => {
  try {
    const response = await API.graphql({ query: runDqfAudit, variables: { dqfId } });
    return response.data?.runDqfAudit;
  } catch (e) {
    console.log(e);
    return null;
  }
};

export const getDqfHeader = async id => {
  try {
    const response = await API.graphql({ query: getDriverQualification, variables: { id } });
    return response.data?.getDriverQualification;
  } catch (e) {
    console.log(e);
    return null;
  }
};

// Reconnect backoff for the live-update subscription: 2s, 4s, 8s, 16s, then every 30s.
export const RECONNECT_DELAYS_MS = [2000, 4000, 8000, 16000];
export const RECONNECT_MAX_MS = 30000;

const reconnectDelay = attempt => RECONNECT_DELAYS_MS[attempt] ?? RECONNECT_MAX_MS;

// Live updates: fires when a DqfAudit flips PROCESSING -> COMPLETE/ERROR. @model subscriptions aren't
// server-filtered by dqfId here, so filter client-side.
//
// CP-867: the previous version subscribed once and its error handler only logged, so a dropped
// WebSocket — laptop sleep, VPN change, network blip, token refresh — killed live updates silently
// and the AI Review spinner never resolved. A UAT tester lost an evening to this on 2026-09-21,
// re-running files that had already completed.
//
// Reconnecting alone is NOT sufficient and must not be mistaken for the fix: a completion that
// fires during the disconnect gap is still missed, leaving the same stuck spinner on a live socket.
// Hence `onReconnect`, which the caller uses to re-fetch the list and close that gap — and the
// polling safety net in the component, which is what actually guarantees convergence.
//
// ⚠️ DO NOT TREAT THE CODE BELOW AS THE RECOVERY MECHANISM. A 4-minute cable-pull on 2026-09-22
// showed this branch never runs on a network drop: Amplify's AppSync client retries the socket
// internally and never surfaces `error` to the observer, so neither the backoff nor onReconnect
// fires. The HAR recorded zero WebSocket attempts across the whole outage, then two successful
// reconnects afterwards with no re-fetch beside them. It is kept because other failure modes
// (token expiry, an explicit server close) may still surface an error, and because reconnecting is
// harmless when it does — but recovery is owned by the polling and wake-up effects in index.js,
// which key off `processing` and the browser's own online/visibilitychange events.
//
// Returns an object with unsubscribe(), matching what the caller already expects of a raw
// subscription, so teardown is unchanged.
export const subscribeDqfAudits = (dqfId, onChange, onReconnect) => {
  let current = null;
  let timer = null;
  let attempt = 0;
  let stopped = false;

  const connect = () => {
    if (stopped) return;
    current = API.graphql(graphqlOperation(onUpdateDqfAudit)).subscribe({
      next: ({ value }) => {
        attempt = 0; // a delivered message proves the socket is healthy again
        const record = value?.data?.onUpdateDqfAudit;
        if (record && record.dqfId === dqfId) onChange(record);
      },
      error: e => {
        // Message first: JSON.stringify of an Error is "{}".
        console.log(`AI Review live updates dropped, reconnecting: ${e?.message || JSON.stringify(e)}`);
        if (stopped) return;
        const delay = reconnectDelay(attempt);
        attempt += 1;
        timer = setTimeout(() => {
          connect();
          // Re-fetch on reconnect: anything that completed while we were disconnected never
          // arrived as a message and would otherwise stay PROCESSING on screen forever.
          onReconnect?.();
        }, delay);
      },
    });
  };

  connect();

  return {
    unsubscribe: () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      current?.unsubscribe?.();
    },
  };
};
